// ============================================================================
// MemberStatusDialog — confirmer actif / inactif (AD-C, P-ADM-18)
// ============================================================================
// Porte du PoC member-active-toggle.tsx sur GsWizardDialog (une etape = une
// confirmation). La desactivation EXIGE un dernier jour actif (borne le prorata
// de masse salariale) — canProceed bloque tant que la date manque ; la
// reactivation efface la date cote serveur. Cote data c'est is_employed qui
// bascule (P-ADM-18), JAMAIS is_active — le libelle parle de statut d'emploi.

import { useEffect, useState } from 'react';
import { GsWizardDialog, GS_COMPONENTS, type GsWizardStep } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminMember } from '../../hooks/useAdminMembers';

export function MemberStatusDialog({
  open,
  member,
  departureDate,
  isPending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  member: AdminMember | null;
  /** Date de depart connue (fiche Compensation) — prefill a la desactivation. */
  departureDate: string | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (input: { employed: boolean; departureDate: string | null }) => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState('');

  // Re-seed a chaque ouverture (parite PoC onOpenChange).
  useEffect(() => {
    if (open) setDate(departureDate ?? '');
  }, [open, departureDate]);

  if (!member) return null;
  const name = member.fullName ?? member.email;
  // Le geste propose est l'INVERSE du statut courant.
  const willDeactivate = member.isEmployed;

  const step: GsWizardStep = {
    id: 'confirm',
    title: willDeactivate
      ? t('gest:admin.members.status.deactivateTitle', { name })
      : t('gest:admin.members.status.activateTitle', { name }),
    canProceed: () => !willDeactivate || !!date,
    content: (
      <div className="flex flex-col gap-4">
        <p className="text-field text-slate-600 dark:text-slate-300">
          {willDeactivate
            ? t('gest:admin.members.status.deactivateBody')
            : t('gest:admin.members.status.activateBody')}
        </p>
        {willDeactivate ? (
          <div className="flex flex-col gap-1.5 sm:max-w-56">
            <label
              htmlFor="member-status-date"
              className="text-meta font-medium text-slate-600 dark:text-slate-300"
            >
              {t('gest:admin.members.status.dateLabel')}
            </label>
            <input
              id="member-status-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
        ) : null}
      </div>
    ),
  };

  return (
    <GsWizardDialog
      open={open}
      onClose={onClose}
      title={
        willDeactivate
          ? t('gest:admin.members.status.deactivateTitle', { name })
          : t('gest:admin.members.status.activateTitle', { name })
      }
      steps={[step]}
      isLoading={isPending}
      previousLabel={t('gest:admin.members.form.back')}
      nextLabel={t('gest:admin.members.form.next')}
      cancelLabel={t('gest:admin.members.form.cancel')}
      pendingLabel={t('gest:admin.members.status.pending')}
      completeLabel={
        willDeactivate
          ? t('gest:admin.members.status.deactivate')
          : t('gest:admin.members.status.activate')
      }
      onComplete={() =>
        onConfirm({
          employed: !willDeactivate,
          departureDate: willDeactivate ? date : null,
        })
      }
    />
  );
}
